import { useState, useEffect } from 'react';
import { SimpleForm, TextInput, ArrayInput, SimpleFormIterator, useEditContext, useInput } from 'react-admin';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { JsonField } from './JsonField';
import { ImageUpload } from '../shared';

interface VariantSkuFormProps {
  toolbar: React.ReactElement;
}

// Images section component that uses useInput hook (must be inside form context)
const ImagesSection = () => {
  const { record } = useEditContext();
  const [images, setImages] = useState<Record<string, string>>({});
  const { field: imageField } = useInput({ source: 'partnerImageUrls' });
  const { field: filesField } = useInput({ source: 'files' });
  const { field: imageKeysField } = useInput({ source: 'imageKeys' });

  // Initialize images from imageField.value (form state) which properly tracks changes
  useEffect(() => {
    const currentImages = imageField.value || record?.partnerImageUrls || {};
    setImages(currentImages);
  }, [imageField.value, record?.partnerImageUrls]);

  const handleImagesChange = (
    updatedImages: Record<string, string>,
    files: Record<string, File>,
    keys: string[]
  ) => {
    setImages(updatedImages);

    // Update form fields using React Admin's field onChange
    // This ensures the data is included in form submission
    imageField.onChange(updatedImages);
    filesField.onChange(files);
    imageKeysField.onChange(keys);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Images
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload and manage product images. Images are stored with unique keys (e.g., main, alt1, alt2).
        </Typography>
        <ImageUpload
          images={images}
          onImagesChange={handleImagesChange}
          maxFileSize={10}
        />
      </CardContent>
    </Card>
  );
};

/**
 * Form for editing variant SKU details
 * Includes product identifiers, custom JSON fields, and image management
 */
export const VariantSkuForm = ({ toolbar }: VariantSkuFormProps) => {
  const { record } = useEditContext();

  return (
    <SimpleForm toolbar={toolbar}>

      <Box sx={{ width: '100%', maxWidth: 1200 }}>
        {/* Display SKU identifier */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Variant Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              SKU: 
              {' '}
              {record?.sku}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Parent SKU: 
              {' '}
              {record?.parentSku}
            </Typography>
          </CardContent>
        </Card>

        {/* Product Identifiers */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Product Identifiers
            </Typography>

            <ArrayInput source="upc" label="UPC">
              <SimpleFormIterator inline disableReordering>
                <TextInput source="" label="UPC" helperText={false} />
              </SimpleFormIterator>
            </ArrayInput>

            <ArrayInput source="mpn" label="MPN (Manufacturer Part Number)">
              <SimpleFormIterator inline disableReordering>
                <TextInput source="" label="MPN" helperText={false} />
              </SimpleFormIterator>
            </ArrayInput>

            <ArrayInput source="gtin" label="GTIN (Global Trade Item Number)">
              <SimpleFormIterator inline disableReordering>
                <TextInput source="" label="GTIN" helperText={false} />
              </SimpleFormIterator>
            </ArrayInput>

            <ArrayInput source="partnerSku" label="Partner SKU">
              <SimpleFormIterator inline disableReordering>
                <TextInput source="" label="Partner SKU" helperText={false} />
              </SimpleFormIterator>
            </ArrayInput>

            <ArrayInput source="partnerParentSku" label="Partner Parent SKU">
              <SimpleFormIterator inline disableReordering>
                <TextInput source="" label="Partner Parent SKU" helperText={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Custom JSON Fields */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Custom Fields
            </Typography>

            <JsonField source="family" label="Family (JSON)" />
            <JsonField source="data" label="Data (JSON)" />
          </CardContent>
        </Card>

        {/* Images Section */}
        <ImagesSection />
      </Box>
    </SimpleForm>
  );
};
